import type { CachedList } from "./offline-db";
import type { QueuedMutation } from "../../shared/domain";

export function applyLocally(
  list: CachedList,
  operation: QueuedMutation,
): CachedList {
  if (list.id !== operation.listId || list.ownerId !== operation.userId)
    throw new Error("Mutation scope mismatch.");
  let items = [...list.items];
  if (operation.type === "add") {
    const payload = operation.payload;
    if (
      payload.parentId === null &&
      items.some(
        (item) => item.parentId === null && item.rootSlot === payload.rootSlot,
      )
    )
      throw new Error("That slot is already filled.");
    items.push({
      id: payload.itemId,
      listId: list.id,
      parentId: payload.parentId,
      rootSlot: payload.rootSlot,
      siblingOrder: payload.siblingOrder,
      title: payload.title,
      completed: false,
      completedAt: null,
      collapsed: false,
      itemVersion: 1,
      metadata: {},
    });
  } else if (operation.type === "rename") {
    const payload = operation.payload;
    items = items.map((item) =>
      item.id === payload.itemId
        ? { ...item, title: payload.title, itemVersion: item.itemVersion + 1 }
        : item,
    );
  } else if (operation.type === "complete") {
    const payload = operation.payload;
    const descendants = new Set<string>([payload.itemId]);
    if (payload.completeSubtree) {
      let changed = true;
      while (changed) {
        changed = false;
        for (const item of items)
          if (
            item.parentId &&
            descendants.has(item.parentId) &&
            !descendants.has(item.id)
          ) {
            descendants.add(item.id);
            changed = true;
          }
      }
    }
    items = items.map((item) =>
      descendants.has(item.id)
        ? {
            ...item,
            completed: payload.completed,
            completedAt: payload.completed ? new Date().toISOString() : null,
            itemVersion: item.itemVersion + 1,
          }
        : item,
    );
  } else if (operation.type === "delete") {
    const payload = operation.payload;
    const removed = new Set<string>([payload.itemId]);
    let changed = true;
    while (changed) {
      changed = false;
      for (const item of items)
        if (
          item.parentId &&
          removed.has(item.parentId) &&
          !removed.has(item.id)
        ) {
          removed.add(item.id);
          changed = true;
        }
    }
    items = items.filter((item) => !removed.has(item.id));
  } else if (operation.type === "reorder") {
    const payload = operation.payload;
    const moving = items.find((item) => item.id === payload.itemId);
    const target = items.find((item) => item.id === payload.overItemId);
    if (!moving || !target || moving.parentId !== target.parentId)
      throw new Error("Things can only be reordered with their siblings.");
    items = items.map((item) => {
      if (item.id === moving.id)
        return {
          ...item,
          rootSlot: target.rootSlot,
          siblingOrder: target.siblingOrder,
          itemVersion: item.itemVersion + 1,
        };
      if (item.id === target.id)
        return {
          ...item,
          rootSlot: moving.rootSlot,
          siblingOrder: moving.siblingOrder,
          itemVersion: item.itemVersion + 1,
        };
      return item;
    });
  } else if (operation.type === "move") {
    const payload = operation.payload;
    if (payload.parentId === payload.itemId)
      throw new Error("A Thing cannot contain itself.");
    items = items.map((item) =>
      item.id === payload.itemId
        ? {
            ...item,
            parentId: payload.parentId,
            rootSlot: payload.rootSlot,
            siblingOrder: payload.siblingOrder,
            itemVersion: item.itemVersion + 1,
          }
        : item,
    );
  } else {
    const payload = operation.payload;
    items = items.map((item) =>
      item.id === payload.itemId
        ? {
            ...item,
            collapsed: payload.collapsed,
            itemVersion: item.itemVersion + 1,
          }
        : item,
    );
  }
  return {
    ...list,
    items,
    optimisticVersion: list.optimisticVersion + 1,
    cachedAt: new Date().toISOString(),
  };
}
